import { useState, useEffect, useRef, useMemo, useCallback, useSyncExternalStore } from "react";
import Big from "big.js";
import { useStoreState } from "../../../store/useStore";
import useFetch from "../../../../hooks/useFetch";
import type {
  ApiResponse,
  RawTicker,
  RawSymbolInfo,
  MarketItem,
  QuoteCurrencyTab,
  MarketContext
} from "../types";

/** 轮询间隔（ms） */
const POLL_INTERVAL = 5000;

// ─── 模块级缓存（跨组件 mount 共享） ──────────────────────────
// 解决 hover 切换二级菜单项导致 SearchMarketWidget 频繁 unmount/mount 后
// 数据全部丢失、骨架屏闪烁、API 重复请求的问题。
//
// 设计要点：
// 1. 数据按 (baseUrl, context) 维度缓存，避免不同站点/语言串号。
// 2. 使用引用计数管理订阅者：每个 hook 实例 mount +1、unmount -1，
//    引用归零后停止轮询；再次 mount 时若已有数据立即可用，并恢复轮询。
// 3. 通过订阅者机制 (notify) 触发各 hook 实例 re-render。

/**
 * 对外暴露的快照（供 useSyncExternalStore 比较）。
 * 必须整体替换引用，任何字段变更都重新生成对象，
 * React 才能通过 Object.is 检测到差异。
 */
interface MarketSnapshot {
  /**
   * 完整初始化是否跑完过一次（symbols + tickers 都成功拉取过）。
   * 重要：必须在 tickers 成功后才置为 true。若只在 symbols 成功后置位，
   * 则 symbols 成功 → tickers 飞行期间 loading 已经派生为 false，
   * 但 dataList 还为空，会渲染出"空态闪一帧"再被数据覆盖。
   */
  initialized: boolean;
  /** 上一次 init 失败（用于让骨架退场，允许下次 mount 重试） */
  loadFailed: boolean;
  dataList: MarketItem[];
}

interface MarketStore {
  snapshot: MarketSnapshot;
  symbolsMap: Record<string, RawSymbolInfo>;
  /** 订阅者计数（mount 中的 hook 实例数量） */
  refCount: number;
  /** 轮询定时器 */
  pollTimer: ReturnType<typeof setInterval> | null;
  /** 内部 listeners，用于触发组件 re-render */
  listeners: Set<() => void>;
  /** 防止并发重复发起 symbols 请求 */
  symbolsLoadingPromise: Promise<boolean> | null;
  /** 防止并发重复发起 ticker 请求 */
  tickerLoadingPromise: Promise<void> | null;
}

const EMPTY_SNAPSHOT: MarketSnapshot = {
  initialized: false,
  loadFailed: false,
  dataList: []
};

const stores = new Map<string, MarketStore>();

const getStore = (key: string): MarketStore => {
  let store = stores.get(key);
  if (!store) {
    store = {
      snapshot: EMPTY_SNAPSHOT,
      symbolsMap: {},
      refCount: 0,
      pollTimer: null,
      listeners: new Set(),
      symbolsLoadingPromise: null,
      tickerLoadingPromise: null
    };
    stores.set(key, store);
  }
  return store;
};

/** 替换 snapshot 引用并通知订阅者。所有写入都必须走这里。 */
const updateSnapshot = (store: MarketStore, patch: Partial<MarketSnapshot>) => {
  store.snapshot = { ...store.snapshot, ...patch };
  store.listeners.forEach((l) => l());
};

// ─── API 端点映射 ────────────────────────────────────────────

/** 币对信息 API（获取有效交易对列表 + 元数据） */
const SYMBOLS_ENDPOINTS: Record<MarketContext, string> = {
  spot: "sapi/v1/basic/symbols",
  futures: "uapi/v3/basic/symbols"
};

/** 行情 API（获取所有 ticker 数据） */
const TICKER_ENDPOINTS: Record<MarketContext, string> = {
  spot: "sapi/v1/market/symbol/all",
  futures: "uapi/v3/market/symbol/all"
};

// ─── 搜索过滤算法 ─────────────────────────────────────────────
// 与 blofin-component WatchList/SearchList 的 filterAndSortSymbolsBySearch 逻辑一致
// 优先级：base currency 精确匹配 > symbol 前缀匹配 > symbol 包含搜索词

function filterAndSortBySearch(items: MarketItem[], search: string): MarketItem[] {
  if (!search.trim()) return items;

  const searchUpper = search.toUpperCase();

  const filtered = items.filter((item) => {
    if (!item.symbol || !item.baseCurrency) return false;
    const symbolUpper = item.symbol.replace("-", "").toUpperCase();
    const baseCurrencyUpper = item.baseCurrency.toUpperCase();
    return baseCurrencyUpper === searchUpper || symbolUpper.includes(searchUpper);
  });

  return filtered.sort((a, b) => {
    const aSymbol = a.symbol.replace("-", "").toUpperCase();
    const bSymbol = b.symbol.replace("-", "").toUpperCase();
    const aBase = a.baseCurrency.toUpperCase();
    const bBase = b.baseCurrency.toUpperCase();

    // 1. base currency 精确匹配排最前
    if (aBase === searchUpper && bBase !== searchUpper) return -1;
    if (aBase !== searchUpper && bBase === searchUpper) return 1;

    // 2. symbol 前缀匹配排第二
    if (aSymbol.startsWith(searchUpper) && !bSymbol.startsWith(searchUpper)) return -1;
    if (!aSymbol.startsWith(searchUpper) && bSymbol.startsWith(searchUpper)) return 1;

    return 0;
  });
}

// ─── 24h 涨跌幅计算（与 WatchList change24 逻辑一致，使用 Big.js 精确运算） ────

function calcChange24(ticker: RawTicker): number {
  const close = Number(ticker.close);
  const open = Number(ticker.open);
  if (!Number.isFinite(close) || !Number.isFinite(open) || open === 0) return 0;
  try {
    const result = Number(new Big(close).minus(open).div(open).times(100));
    return Number.isFinite(result) ? result : 0;
  } catch {
    return 0;
  }
}

// ─── Hook ─────────────────────────────────────────────────────

interface UseSearchMarketReturn {
  /** 当前激活 tab（按 quote currency 过滤）— 仅 Spot 有 tab 模式时有意义 */
  activeTab: QuoteCurrencyTab;
  /** 切换 tab */
  setActiveTab: (tab: QuoteCurrencyTab) => void;
  /** 搜索关键词 */
  search: string;
  /** 更新搜索关键词 */
  setSearch: (val: string) => void;
  /** 过滤后的结果列表 */
  results: MarketItem[];
  /** 是否正在加载（首次） */
  loading: boolean;
  /** 是否处于搜索模式 */
  isSearching: boolean;
  /** 默认展示列表（无搜索时按成交额 top） */
  topList: MarketItem[];
}

/**
 * useSearchMarket — MegaMenu 搜索行情 hook
 *
 * 完全对齐 blofin-component WatchList 的数据逻辑：
 * 1. 先加载币对列表（/basic/symbols）获取有效交易对 + 元数据（logo、quote_currency 等）
 * 2. 再加载行情数据（/market/symbol/all），用币对列表过滤 & 合并
 * 3. 5 秒轮询刷新行情
 *
 * @param visible 面板是否可见（控制轮询启停）
 * @param context 市场上下文：spot 或 futures
 * @param externalQuoteCurrency 外部指定 quote currency（传入后跳过内部 Tab，如 "USDT"、"USDC"、"USD"）
 */
const useSearchMarket = (
  visible: boolean,
  context: MarketContext = "spot",
  externalQuoteCurrency?: string
): UseSearchMarketReturn => {
  const { baseUrl } = useStoreState();
  const { get } = useFetch(baseUrl);

  const [activeTab, setActiveTab] = useState<QuoteCurrencyTab>("USDT");
  const [search, setSearch] = useState("");

  // 用 ref 保存 get 函数，保证 callback 引用稳定（useFetch 每次渲染返回新引用）
  const fetchRef = useRef(get);
  fetchRef.current = get;

  // 实际生效的筛选值：外部指定 > 内部 Tab
  const effectiveFilter = externalQuoteCurrency || activeTab;

  // 模块级 store（按 baseUrl + context 维度共享缓存）
  const storeKey = `${baseUrl || ""}::${context}`;
  const store = useMemo(() => getStore(storeKey), [storeKey]);

  // 通过 useSyncExternalStore 订阅 store 变化，重 mount 后能立即拿到缓存数据
  const subscribe = useCallback(
    (cb: () => void) => {
      store.listeners.add(cb);
      return () => {
        store.listeners.delete(cb);
      };
    },
    [store]
  );
  const snapshot = useSyncExternalStore(
    subscribe,
    () => store.snapshot,
    () => store.snapshot
  );
  const { dataList, initialized, loadFailed } = snapshot;

  // 整个 init 流程跑完前（包括 symbols 成功但 tickers 还没回来）都保持骨架；
  // 失败时由 loadFailed 让骨架退场转空态。
  const loading = !initialized && !loadFailed;

  // ─── 1. 加载币对列表（共享 + 防并发） ─────────────────────
  const loadSymbols = useCallback(async () => {
    // 用内部 symbolsMap 作为"已加载过"的判断标准（不再依赖 snapshot.symbolsLoaded）。
    // initialized 现在表示"完整 init 跑完"，不再代表 symbols 单步状态。
    if (Object.keys(store.symbolsMap).length > 0) return true;
    if (store.symbolsLoadingPromise) return store.symbolsLoadingPromise;

    const promise = (async () => {
      try {
        const endpoint = SYMBOLS_ENDPOINTS[context];
        const res = await fetchRef.current<ApiResponse<RawSymbolInfo[]>>(endpoint);
        if (res && res.code === 200 && Array.isArray(res.data)) {
          const map: Record<string, RawSymbolInfo> = {};
          for (const item of res.data) {
            map[item.symbol] = item;
          }
          // 仅缓存 symbolsMap，不动 snapshot——避免 loading 提前 false 触发空态闪烁。
          // initialized 由 fetchTickers 成功后统一写入。
          store.symbolsMap = map;
          return true;
        }
      } catch {
        // baseUrl 为空或请求失败时 useFetch 会 reject
      }
      // 失败：标记 loadFailed=true 让骨架退场（呈现空态而非永久旋转）。
      // 但仅当还有订阅者时才写入——若用户已关闭菜单（refCount=0），
      // 不写入失败标记，避免下次打开 snapshot 仍带 loadFailed=true
      // 导致第一次渲染闪一帧"空态"再切到骨架。
      if (store.refCount > 0) {
        updateSnapshot(store, { loadFailed: true });
      }
      return false;
    })();

    store.symbolsLoadingPromise = promise;
    try {
      return await promise;
    } finally {
      store.symbolsLoadingPromise = null;
    }
  }, [context, store]);

  // ─── 2. 加载行情并与币对信息合并（共享 + 防并发） ───────
  const fetchTickers = useCallback(async () => {
    if (Object.keys(store.symbolsMap).length === 0) return;
    if (store.tickerLoadingPromise) return store.tickerLoadingPromise;

    const promise = (async () => {
      try {
        const endpoint = TICKER_ENDPOINTS[context];
        const res = await fetchRef.current<ApiResponse<RawTicker[]>>(endpoint);
        if (res && res.code === 200 && Array.isArray(res.data)) {
          const symbolsMap = store.symbolsMap;
          const items: MarketItem[] = res.data
            .filter((ticker) => symbolsMap[ticker.symbol])
            .map((ticker) => {
              const info = symbolsMap[ticker.symbol];
              const currencyType: string | undefined = info.currency_type || undefined;
              const volume =
                currencyType === "usd" ? Number(ticker.quantity) || 0 : Number(ticker.amount) || 0;
              return {
                symbol: ticker.symbol,
                baseCurrency: info.base_currency,
                quoteCurrency: info.quote_currency,
                price: Number(ticker.close),
                change24: calcChange24(ticker),
                volume,
                logo: info.logo || "",
                sort: info.sort ?? 9999,
                spotType: info.spot_type || undefined,
                contractType: currencyType
              };
            });
          // tickers 成功才标记 initialized=true：loading 由此退场，
          // dataList 和 initialized 在同一次 snapshot 写入中完成 → 无空态闪烁。
          updateSnapshot(store, { dataList: items, initialized: true, loadFailed: false });
        }
      } catch {
        // 静默处理；轮询失败保留旧数据。
        // 仅当从未成功过（无缓存数据）且仍有订阅者时，标记失败让骨架退场。
        if (
          store.refCount > 0 &&
          !store.snapshot.initialized &&
          store.snapshot.dataList.length === 0
        ) {
          updateSnapshot(store, { loadFailed: true });
        }
      }
    })();

    store.tickerLoadingPromise = promise;
    try {
      await promise;
    } finally {
      store.tickerLoadingPromise = null;
    }
  }, [context, store]);

  // ─── 订阅与轮询（引用计数控制） ───────────────────────────
  // 多个 widget 实例共享同一个 store + 一份轮询定时器。
  // refCount 归零后停止轮询，但保留缓存数据，再次进入时立即可用。
  useEffect(() => {
    if (!visible) return;

    store.refCount += 1;
    let cancelled = false;

    const init = async () => {
      const ok = await loadSymbols();
      if (cancelled || !ok) return;
      // 仅当无任何缓存数据时才阻塞首屏；否则后台静默刷新。
      if (store.snapshot.dataList.length === 0) {
        await fetchTickers();
      } else {
        fetchTickers();
      }
    };

    init();

    if (!store.pollTimer) {
      store.pollTimer = setInterval(() => {
        fetchTickers();
      }, POLL_INTERVAL);
    }

    return () => {
      cancelled = true;
      store.refCount = Math.max(0, store.refCount - 1);
      if (store.refCount === 0) {
        if (store.pollTimer) {
          clearInterval(store.pollTimer);
          store.pollTimer = null;
        }
        // 清掉失败标记，避免再次打开时第一次渲染出现 "空态 → 骨架" 的闪烁：
        // 下次打开时 snapshot 已经是干净的（loadFailed=false），第一次渲染
        // 直接派生 loading=true → 骨架立即显示。
        if (store.snapshot.loadFailed) {
          updateSnapshot(store, { loadFailed: false });
        }
      }
    };
  }, [visible, loadSymbols, fetchTickers, store]);

  // ─── 筛选逻辑（与 WatchList 一致） ────────────────────────
  // Spot：按 spot_type 筛选（"USDT" / "USDC"）
  // Futures：按 formatted_contract_type 筛选（"usdt" / "usdc" / "usd"）

  const filteredList = useMemo(() => {
    if (context === "spot") {
      // Spot 用 spot_type 字段筛选（与 WatchList item.spot_type === spotType 一致）
      return dataList.filter(
        (item) => item.spotType?.toUpperCase() === effectiveFilter.toUpperCase()
      );
    } else {
      // Futures 用 contractType 字段筛选（与 WatchList item.formatted_contract_type === futuresType 一致）
      // effectiveFilter 来自 hoveredItem: "USDT" → "usdt", "USDC" → "usdc", "USD" → "usd"
      return dataList.filter((item) => item.contractType === effectiveFilter.toLowerCase());
    }
  }, [dataList, effectiveFilter, context]);

  // ─── 搜索结果 ─────────────────────────────────────────────

  const isSearching = search.trim().length > 0;

  const results = useMemo(() => {
    if (!isSearching) return [];
    return filterAndSortBySearch(filteredList, search);
  }, [filteredList, search, isSearching]);

  // 默认列表：按 sort 字段降序（值越大越靠前，与 WatchList 默认顺序一致）
  const topList = useMemo(() => {
    return [...filteredList].sort((a, b) => b.sort - a.sort);
  }, [filteredList]);

  return {
    activeTab,
    setActiveTab,
    search,
    setSearch,
    results,
    loading,
    isSearching,
    topList
  };
};

export default useSearchMarket;
